import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, Reorder, motion, useDragControls } from "framer-motion";
import { toast } from "sonner";
import { Plus, GripVertical, ChevronDown, Pencil, Trash2 } from "lucide-react";
import { AppFrame } from "@/components/AppFrame";
import { MedicineForm, ALL_DAYS } from "@/components/MedicineForm";
import { api, cachedGet, getFresh, fmt12, fmtDayMonth, todayStr } from "@/lib/api";
import { useProfile } from "@/context/ProfileContext";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const COLLAPSE_KEY = "pb_collapsed_groups";
const UNGROUPED = "__ungrouped"; // sentinel: the ungrouped section's slot in the group order

const daysSummary = (days = ALL_DAYS) =>
  days.length === 7 ? "Every day" : days.map((d) => DAY_NAMES[d]).join(", ");

export const rangeLabel = (m) => {
  if (m.start_date && m.end_date) return `${fmtDayMonth(m.start_date)} – ${fmtDayMonth(m.end_date)}`;
  if (m.end_date) return `Until ${fmtDayMonth(m.end_date)}`;
  if (m.start_date && m.start_date > todayStr()) return `From ${fmtDayMonth(m.start_date)}`;
  return null;
};

const Row = ({ m, onOpen, onCommit, readOnly }) => {
  const controls = useDragControls();
  const meta = [rangeLabel(m), (m.days || ALL_DAYS).length < 7 ? daysSummary(m.days) : null, m.notes].filter(Boolean);
  return (
    <Reorder.Item
      value={m}
      dragListener={false}
      dragControls={controls}
      onDragEnd={onCommit}
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.18 }}
      whileDrag={{ scale: 1.02, zIndex: 30 }}
      data-testid={`medicine-card-${m.med_id}`}
      className="relative overflow-hidden rounded-2xl border border-slate-800/80 bg-slate-900/55 py-2 pl-3 pr-2.5 transition-colors duration-200 active:border-sky-500/30"
    >
      <div className="flex items-center gap-1">
        <div
          data-testid={`open-medicine-${m.med_id}`}
          onClick={onOpen}
          className="min-w-0 flex-1 cursor-pointer py-1"
        >
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-[16px] font-semibold tracking-tight text-slate-50">{m.name}</p>
            <span className="flex shrink-0 items-center gap-1" data-testid={`medicine-times-${m.med_id}`}>
              {m.times.map((t) => (
                <span
                  key={t}
                  className="rounded-full bg-sky-500/10 px-2 py-[1px] text-[12px] font-medium text-sky-300/90 ring-1 ring-inset ring-sky-500/20"
                >
                  {fmt12(t)}
                </span>
              ))}
            </span>
          </div>
          {meta.length > 0 && (
            <p className="mt-0.5 truncate text-[12.5px] text-slate-500" data-testid={`medicine-meta-${m.med_id}`}>
              {meta.join(" · ")}
            </p>
          )}
        </div>
        {!readOnly && (
          <span
            data-testid={`drag-handle-${m.med_id}`}
            onPointerDown={(e) => controls.start(e)}
            className="flex h-10 w-6 shrink-0 cursor-grab touch-none items-center justify-center text-slate-700 transition-colors duration-200 hover:text-slate-500"
          >
            <GripVertical className="h-4 w-4" />
          </span>
        )}
      </div>
    </Reorder.Item>
  );
};

/** A group card. Long-pressing the header name starts a drag to reorder whole groups. */
const GroupSection = ({ name, count, isOpen, onToggle, onRename, onCommit, readOnly, draggable, showHeader, children }) => {
  const controls = useDragControls();
  const isUngrouped = name === UNGROUPED;
  const label = isUngrouped ? "Ungrouped" : name;
  const timer = useRef(null);
  const dragging = useRef(false);
  const origin = useRef(null);

  const hold = (e) => {
    if (!draggable) return;
    const ev = e;
    origin.current = { x: e.clientX, y: e.clientY };
    timer.current = setTimeout(() => {
      timer.current = null;
      dragging.current = true;
      controls.start(ev);
    }, 220);
  };
  const drift = (e) => {
    if (!timer.current || !origin.current) return;
    if (Math.hypot(e.clientX - origin.current.x, e.clientY - origin.current.y) > 14) release();
  };
  const release = () => {
    clearTimeout(timer.current);
    timer.current = null;
  };

  return (
    <Reorder.Item
      value={name}
      dragListener={false}
      dragControls={controls}
      onDragEnd={() => {
        onCommit();
        setTimeout(() => (dragging.current = false), 80);
      }}
      whileDrag={{ scale: 1.01, zIndex: 40 }}
      data-testid={`group-section-${name}`}
      className="rounded-2xl"
    >
      {showHeader && (
      <div className="mb-1.5 flex items-center gap-2">
        <button
          data-testid={`group-toggle-${name}`}
          onPointerDown={hold}
          onPointerMove={drift}
          onPointerUp={release}
          onPointerCancel={release}
          onContextMenu={(e) => e.preventDefault()}
          onClick={() => {
            if (dragging.current) {
              dragging.current = false;
              return;
            }
            onToggle();
          }}
          className={`flex min-w-0 select-none items-center gap-1.5 py-0.5 ${draggable ? "touch-none" : ""}`}
        >
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-teal-300/70 transition-transform duration-200 ${
              isOpen ? "" : "-rotate-90"
            }`}
          />
          <span className="truncate text-[14px] font-semibold tracking-tight text-teal-300">{label}</span>
          <span className="text-[11px] text-teal-300/50">{count}</span>
        </button>
        <span className="flex-1" />
        {!isUngrouped && !readOnly && (
          <button
            data-testid={`rename-group-${name}`}
            onClick={() => onRename()}
            className="flex h-7 w-7 items-center justify-center rounded-full text-slate-600 transition-colors duration-200 hover:bg-slate-800 hover:text-slate-300 active:scale-90"
          >
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      )}
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ height: { duration: 0.22, ease: [0.4, 0, 0.2, 1] }, opacity: { duration: 0.14 } }}
            style={{ overflow: "hidden" }}
          >
            {children}
          </motion.div>
        )}
      </AnimatePresence>
    </Reorder.Item>
  );
};

export default function Meds() {
  const { active } = useProfile();
  const location = useLocation();
  const navigate = useNavigate();
  const readOnly = active && !active.is_self && active.access === "view";
  const [meds, setMeds] = useState(() => cachedGet("/medicines") || []);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"));
  const [groupEdit, setGroupEdit] = useState(null); // { name, next }
  const [groupDelete, setGroupDelete] = useState(null);
  const [escapeLock, setEscapeLock] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const [groupsLoaded, setGroupsLoaded] = useState(false);
  const returnTo = location.state?.from;

  const load = async () => {
    const data = await getFresh("/medicines");
    setMeds(data);
    setHydrated(true);
    return data;
  };

  useEffect(() => {
    load().then((data) => {
      const target = location.state?.editMedId;
      if (!target) return;
      const m = data.find((x) => x.med_id === target);
      if (m) {
        setEditing(m);
        setOpen(true);
      }
    });
  }, [location.state]);

  const closeDialog = (next) => {
    setOpen(next);
    if (!next && returnTo) navigate(returnTo);
  };

  const toggleGroup = (g) => {
    setCollapsed((c) => {
      const next = { ...c, [g]: !c[g] };
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const commitOrder = async (list) => {
    try {
      await api.post("/medicines/reorder", { med_ids: list.map((m) => m.med_id) });
    } catch {
      toast.error("Could not save the new order");
      load();
    }
  };

  const byGroup = useMemo(() => {
    const map = new Map();
    meds.forEach((m) => {
      const g = (m.group || "").trim();
      if (!map.has(g)) map.set(g, []);
      map.get(g).push(m);
    });
    return map;
  }, [meds]);

  const names = useMemo(() => [...byGroup.keys()].filter(Boolean), [byGroup]);
  const ungrouped = byGroup.get("") || [];

  const [order, setOrder] = useState(() => cachedGet("/groups")?.order || []);
  const [groups, setGroups] = useState(() => cachedGet("/groups")?.groups || []);
  const showHeaders = groups.length > 0;

  const loadGroups = async () => {
    const data = await getFresh("/groups");
    setOrder(data.order);
    setGroups(data.groups);
    setGroupsLoaded(true);
    return data;
  };

  useEffect(() => {
    loadGroups().catch(() => {});
  }, []);

  // any group that only exists on a medicine (or was just typed) still gets a section
  useEffect(() => {
    if (!hydrated || !groupsLoaded) return;
    setOrder((prev) => {
      const missing = names.filter((n) => !prev.includes(n));
      return missing.length ? [...prev, ...missing] : prev;
    });
    setCollapsed((c) => {
      const known = new Set([...names, ...order, UNGROUPED]);
      const live = Object.fromEntries(Object.entries(c).filter(([k]) => known.has(k)));
      if (Object.keys(live).length === Object.keys(c).length) return c;
      localStorage.setItem(COLLAPSE_KEY, JSON.stringify(live));
      return live;
    });
  }, [names, order, hydrated, groupsLoaded]);

  const itemsOf = (name) => (name === UNGROUPED ? ungrouped : byGroup.get(name) || []);
  const medOrder = () => order.flatMap(itemsOf);

  const saveGroupOrder = async (list) => {
    try {
      await api.post("/groups/reorder", { names: list });
    } catch {
      toast.error("Could not save the group order");
      loadGroups();
    }
  };

  const reorderSection = (name, list) => {
    setMeds(order.flatMap((n) => (n === name ? list : itemsOf(n))));
  };

  const rowsOf = (name, items) => (
    <Reorder.Group axis="y" values={items} onReorder={(list) => reorderSection(name, list)} className="space-y-2">
      {items.map((m) => (
        <Row
          key={m.med_id}
          m={m}
          readOnly={readOnly}
          onCommit={() => commitOrder(medOrder())}
          onOpen={() => {
            if (readOnly) return;
            setEditing(m);
            setOpen(true);
          }}
        />
      ))}
    </Reorder.Group>
  );

  return (
    <AppFrame title="Medicines">
      {meds.length === 0 && groups.length === 0 ? (
        <p className="py-14 text-center text-[15px] text-slate-400" data-testid="empty-medicines">
          No medicines yet.
        </p>
      ) : (
        <div data-testid="medicine-list">
          <Reorder.Group
            axis="y"
            values={order}
            onReorder={setOrder}
            className="space-y-3"
            data-testid="group-reorder"
          >
            {order.map((name) => {
              const items = itemsOf(name);
              if (name === UNGROUPED && items.length === 0) return null;
              const isOpen = !showHeaders || !collapsed[name];
              return (
                <GroupSection
                  key={name}
                  name={name}
                  count={items.length}
                  isOpen={isOpen}
                  readOnly={readOnly}
                  showHeader={showHeaders}
                  draggable={!readOnly && showHeaders && order.length > 1}
                  onToggle={() => toggleGroup(name)}
                  onRename={() => setGroupEdit({ name, next: name })}
                  onCommit={() => saveGroupOrder(order)}
                >
                  {items.length > 0 ? rowsOf(name, items) : null}
                </GroupSection>
              );
            })}
          </Reorder.Group>
        </div>
      )}

      {!readOnly && (
        <button
          data-testid="add-medicine-btn"
          onClick={() => {
            setEditing(null);
            setOpen(true);
          }}
          className="mt-4 flex h-12 w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-sky-500 to-blue-600 text-[15px] font-semibold text-white shadow-[0_0_22px_rgba(56,189,248,0.28)] transition-transform duration-150 active:scale-95"
        >
          <Plus className="h-5 w-5" /> Add
        </button>
      )}

      <Dialog open={open} onOpenChange={closeDialog}>
        <DialogContent
          className="max-h-[88vh] max-w-[350px] overflow-y-auto rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => {
            if (escapeLock) e.preventDefault();
          }}
        >
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg">{editing ? "Edit medicine" : "New medicine"}</DialogTitle>
            <DialogDescription className="sr-only">Name, times, group and schedule.</DialogDescription>
          </DialogHeader>
          <MedicineForm
            med={editing}
            groups={groups}
            onGroupsChanged={() => {
              load();
              loadGroups();
            }}
            onInlineEditChange={setEscapeLock}
            onSaved={({ keepOpen }) => {
              load();
              if (!keepOpen) closeDialog(false);
            }}
            onDeleted={() => {
              load();
              closeDialog(false);
            }}
          />
        </DialogContent>
      </Dialog>
      <Dialog open={!!groupEdit} onOpenChange={(o) => !o && setGroupEdit(null)}>
        <DialogContent className="max-w-[340px] rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg">Rename group</DialogTitle>
            <DialogDescription className="sr-only">Give this group a new name.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <Input
              data-testid="group-rename-input"
              value={groupEdit?.next || ""}
              onChange={(e) => setGroupEdit((g) => ({ ...g, next: e.target.value }))}
              className="h-12 flex-1 rounded-xl border-slate-800 bg-slate-900/70 text-[15px]"
            />
            <button
              data-testid="group-rename-save-btn"
              onClick={async () => {
                const next = (groupEdit.next || "").trim();
                if (!next) return toast.error("Enter a group name");
                try {
                  await api.patch("/groups", { name: groupEdit.name, new_name: next });
                  setGroupEdit(null);
                  load();
                  loadGroups();
                } catch {
                  toast.error("Could not rename the group");
                }
              }}
              className="h-12 shrink-0 rounded-xl bg-gradient-to-r from-sky-500 to-blue-600 px-5 text-[14px] font-semibold text-white active:scale-95"
            >
              Save
            </button>
          </div>
          <button
            data-testid={`delete-group-${groupEdit?.name}`}
            onClick={() => {
              setGroupDelete(groupEdit.name);
              setGroupEdit(null);
            }}
            className="mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-full text-[14px] font-semibold text-red-400/90 hover:bg-red-500/10 active:scale-95"
          >
            <Trash2 className="h-4 w-4" /> Remove group
          </button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!groupDelete} onOpenChange={(o) => !o && setGroupDelete(null)}>
        <DialogContent className="max-w-[340px] rounded-2xl border-slate-800 bg-slate-950/95 backdrop-blur-xl">
          <DialogHeader className="text-left">
            <DialogTitle className="text-lg">Remove “{groupDelete}”?</DialogTitle>
            <DialogDescription className="text-[15px] text-slate-400">
              The medicines stay — they just become ungrouped.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2">
            <button
              data-testid="cancel-group-delete-btn"
              onClick={() => setGroupDelete(null)}
              className="h-12 flex-1 rounded-full border border-slate-700 text-[15px] font-semibold text-slate-300 active:scale-95"
            >
              Cancel
            </button>
            <button
              data-testid="confirm-group-delete-btn"
              onClick={async () => {
                try {
                  await api.delete(`/groups/${encodeURIComponent(groupDelete)}`);
                  setGroupDelete(null);
                  load();
                  loadGroups();
                } catch {
                  toast.error("Could not remove the group");
                }
              }}
              className="h-12 flex-1 rounded-full bg-red-500/15 text-[15px] font-semibold text-red-300 ring-1 ring-red-500/30 active:scale-95"
            >
              Remove
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </AppFrame>
  );
}
